import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { News, NewsDivision } from '../../../models/news.model';
import { onError } from '../../../utils/error.util';
import { createNews, getNews, updateNews } from '../../../services/news.service';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';

import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

const NewsSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  htmlContent: Yup.string().required('Description is required'),
  division: Yup.string().oneOf([NewsDivision.CCL, NewsDivision.TSF, NewsDivision.Company]).required('Division is required'),
  displayDate: Yup.string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Choose a valid date')
    .required('Date is required'),
});

function emptyNewsFormState(): News {
  return {
    id: '',
    title: '',
    htmlContent: '',
    division: NewsDivision.CCL,
    createdAt: 0,
    updatedAt: 0,
    displayDate: format(Date.now(), 'yyyy-MM-dd'),
  };
}

export default function NewsFormContainer() {
  const navigate = useNavigate();
  const { newsId } = useParams();
  const [news, setNews] = useState<News>(() => emptyNewsFormState());
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function onLoad() {
      if (!newsId) {
        setIsLoading(false);
        return;
      }

      if (newsId === 'new') {
        setNews(emptyNewsFormState());
        setIsLoading(false);
        return;
      }

      try {
        const response = await getNews(newsId);
        setNews(response);
      } catch (e) {
        onError(e);
      }

      setIsLoading(false);
    }

    onLoad();
  }, [newsId]);

  const onSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      if (newsId === 'new') {
        await createNews({
          title: values.title,
          htmlContent: values.htmlContent,
          division: values.division,
          displayDate: values.displayDate,
        });
      } else {
        await updateNews({
          ...news,
          title: values.title,
          htmlContent: values.htmlContent,
          division: values.division,
          displayDate: values.displayDate,
        });
      }
      navigate('/news');
    } catch (e: any) {
      onError(e);
      setIsSubmitting(false);
    }
  };

  function renderForm() {
    return (
      <Container>
        <Formik
          initialValues={news}
          validationSchema={NewsSchema}
          onSubmit={onSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form>
              <Box sx={{ mb: 3 }}>
                <h1>{newsId === 'new' ? 'Create News' : 'Edit News'}</h1>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="title"
                    name="title"
                    label="Title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    id="displayDate"
                    name="displayDate"
                    label="Date"
                    type="date"
                    value={values.displayDate || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={touched.displayDate && Boolean(errors.displayDate)}
                    helperText={touched.displayDate && errors.displayDate}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth error={touched.division && Boolean(errors.division)}>
                    <InputLabel>Division</InputLabel>
                    <Select
                      id="division"
                      name="division"
                      value={values.division || ''}
                      label="Division"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value={NewsDivision.CCL}>CCL</MenuItem>
                      <MenuItem value={NewsDivision.TSF}>TSF</MenuItem>
                      <MenuItem value={NewsDivision.Company}>Company</MenuItem>
                    </Select>
                    {touched.division && errors.division && <FormHelperText>{errors.division}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={7}
                    id="htmlContent"
                    name="htmlContent"
                    label="Description"
                    value={values.htmlContent}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.htmlContent && Boolean(errors.htmlContent)}
                    helperText={touched.htmlContent && errors.htmlContent}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/news')} disabled={isSubmitting}>
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Container>
    );
  }

  return <main id="main">{isLoading ? <CircularProgressComponent /> : renderForm()}</main>;
}

